import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DateField } from '@/components/dateFields';
import { appDate } from '@/utils/appDateFormat';
import {
  BusinessRoleView,
  BusinessRoleType,
  EmployeeSimpleView,
  BulkAssignmentEmployeeResult,
  searchEmployeesForAssignment,
  bulkAssignRole,
  getRoleFit,
} from '../../../api/General/businessRoles';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle, DialogDescription,
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import { todayLocalDateParam } from '@/utils/apiDate';

interface BulkAssignmentModalProps {
  role: BusinessRoleView;
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
}

const roleTypeStyles: Record<BusinessRoleType, string> = {
  [BusinessRoleType.Department]: 'bg-primary/10 text-primary',
  [BusinessRoleType.Project]: 'bg-purple-100 text-purple-700',
  [BusinessRoleType.Temporary]: 'bg-orange-100 text-orange-700',
};

const roleTypeLabels: Record<BusinessRoleType, string> = {
  [BusinessRoleType.Department]: 'Department',
  [BusinessRoleType.Project]: 'Project',
  [BusinessRoleType.Temporary]: 'Temporary',
};

type ModalStep = 'select' | 'confirm' | 'result';

export default function BulkAssignmentModal({ role, open, onClose, onCompleted }: BulkAssignmentModalProps) {
  const { t } = useTranslation();

  // Employee search
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [searchResults, setSearchResults] = useState<EmployeeSimpleView[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Selected employees
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeSimpleView[]>([]);

  // Form
  const [effectiveDate, setEffectiveDate] = useState(() => todayLocalDateParam());
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [forceAssign, setForceAssign] = useState(false);

  // Step & status
  const [step, setStep] = useState<ModalStep>('select');
  const [assigning, setAssigning] = useState(false);
  const [results, setResults] = useState<BulkAssignmentEmployeeResult[]>([]);
  const [error, setError] = useState('');

  // Per-employee fit shown in the confirm step (Q3). Informational — bulk never blocks.
  const [fits, setFits] = useState<Record<string, number>>({});

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setEmployeeSearch('');
      setSearchResults([]);
      setSelectedEmployees([]);
      setEffectiveDate(todayLocalDateParam());
      setExpiryDate('');
      setNotes('');
      setForceAssign(false);
      setStep('select');
      setResults([]);
      setError('');
      setFits({});
    }
  }, [open]);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced employee search
  useEffect(() => {
    if (!employeeSearch || employeeSearch.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await searchEmployeesForAssignment(employeeSearch);
        if (result.errors.length === 0) {
          // Filter out already selected employees
          const filtered = result.payload.filter(
            emp => !selectedEmployees.some(s => s.id === emp.id)
          );
          setSearchResults(filtered);
          setShowDropdown(true);
        }
      } catch { /* silent */ }
    }, 300);

    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [employeeSearch, selectedEmployees]);

  const handleSelectEmployee = (emp: EmployeeSimpleView) => {
    setSelectedEmployees(prev => [...prev, emp]);
    setEmployeeSearch('');
    setShowDropdown(false);
  };

  const handleRemoveEmployee = (empId: string) => {
    setSelectedEmployees(prev => prev.filter(e => e.id !== empId));
  };

  const handleProceedToConfirm = async () => {
    if (selectedEmployees.length === 0) return;
    setStep('confirm');

    try {
      const fetched = await Promise.all(
        selectedEmployees.map(async (emp) => {
          try {
            const result = await getRoleFit(role.id, emp.id);
            if (result.errors.length === 0) {
              return [emp.id, Math.round(result.payload.coveragePercent)] as const;
            }
          } catch { /* silent */ }
          return null;
        })
      );
      setFits(Object.fromEntries(fetched.filter((entry): entry is readonly [string, number] => entry !== null)));
    } catch { /* silent */ }
  };

  const handleAssign = useCallback(async () => {
    setAssigning(true);
    setError('');
    try {
      const result = await bulkAssignRole({
        businessRoleId: role.id,
        employeeIds: selectedEmployees.map(e => e.id),
        effectiveDate,
        expiryDate: expiryDate || undefined,
        notes: notes || undefined,
        forceAssign,
      });

      if (result.errors.length === 0) {
        setResults(result.payload.results);
        setStep('result');
        onCompleted();
      } else {
        setError(result.errors[0]?.message || t('businessRoles.errors.saveFailed'));
      }
    } catch {
      setError(t('businessRoles.errors.saveFailed'));
    } finally {
      setAssigning(false);
    }
  }, [role.id, selectedEmployees, effectiveDate, expiryDate, notes, forceAssign, onCompleted, t]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t('businessRoles.bulkAssign')}</DialogTitle>
          <DialogDescription>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${roleTypeStyles[role.type]}`}>
              {t(`businessRoles.type${roleTypeLabels[role.type]}`)}
            </span>{' '}
            {role.name}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {step === 'select' && (
            <div className="space-y-4">
              {/* Employee search */}
              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('businessRoles.bulkSelectEmployees')}
                </label>
                <input
                  type="text"
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  placeholder={t('businessRoles.searchEmployee')}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
                {showDropdown && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {searchResults.map((emp) => (
                      <button
                        key={emp.id}
                        onClick={() => handleSelectEmployee(emp)}
                        className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2"
                      >
                        <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-primary">
                            {emp.fullName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground truncate">{emp.fullName}</p>
                          {emp.department && (
                            <p className="text-xs text-muted-foreground">{emp.department}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected employees list */}
              {selectedEmployees.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase mb-2">
                    {t('businessRoles.bulkSelectedCount', { count: selectedEmployees.length })}
                  </p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {selectedEmployees.map((emp) => (
                      <div key={emp.id} className="flex items-center justify-between p-2 rounded-lg border border-border bg-muted">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-medium text-primary">
                              {emp.fullName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-foreground truncate">{emp.fullName}</p>
                            {emp.department && <p className="text-xs text-muted-foreground">{emp.department}</p>}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 hover:text-destructive"
                          onClick={() => handleRemoveEmployee(emp.id)}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Date fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t('businessRoles.effectiveDate')}</label>
                  <DateField
                    value={effectiveDate}
                    onChange={(v) => setEffectiveDate(v)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('businessRoles.expiryDate')}
                    <span className="text-muted-foreground font-normal ms-1">({t('common.optional')})</span>
                  </label>
                  <DateField
                    value={expiryDate}
                    onChange={(v) => setExpiryDate(v)}
                    min={effectiveDate || undefined}
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('businessRoles.assignmentNotes')}
                  <span className="text-muted-foreground font-normal ms-1">({t('common.optional')})</span>
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('businessRoles.assignmentNotesPlaceholder')}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>

              {/* Warning about conflicts */}
              <div className="bg-warning/5 border border-warning/20 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-xs text-warning">{t('businessRoles.bulkConflictWarning')}</p>
                </div>
              </div>
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-4">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-primary font-medium mb-2">
                  {t('businessRoles.bulkConfirmMessage', { count: selectedEmployees.length, role: role.name })}
                </p>
                <div className="text-xs text-primary space-y-1">
                  <p>{t('businessRoles.effectiveDate')}: {appDate(effectiveDate)}</p>
                  {expiryDate && <p>{t('businessRoles.expiryDate')}: {appDate(expiryDate)}</p>}
                  {notes && <p>{t('businessRoles.assignmentNotes')}: {notes}</p>}
                </div>
              </div>

              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {selectedEmployees.map((emp) => (
                  <div key={emp.id} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-border">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-primary">
                          {emp.fullName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{emp.fullName}</p>
                        {emp.department && <p className="text-xs text-muted-foreground">{emp.department}</p>}
                      </div>
                    </div>
                    {fits[emp.id] !== undefined && (
                      <span
                        data-testid={`bulk-fit-${emp.id}`}
                        className={`text-xs font-medium whitespace-nowrap ${
                          fits[emp.id] >= 100
                            ? 'text-success'
                            : fits[emp.id] >= 50
                              ? 'text-amber-600'
                              : 'text-destructive'
                        }`}
                      >
                        {fits[emp.id] < 50
                          ? t('roleFit.bulkPoorFit', { percent: fits[emp.id] })
                          : t('roleFit.bulkFit', { percent: fits[emp.id] })}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {error && (
                <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-3">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}
            </div>
          )}

          {step === 'result' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{results.length}</p>
                  <p className="text-xs text-muted-foreground">{t('businessRoles.bulkTotal')}</p>
                </div>
                <div className="bg-success/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-success">{results.filter(r => r.success).length}</p>
                  <p className="text-xs text-success">{t('businessRoles.bulkSucceeded')}</p>
                </div>
                <div className="bg-destructive/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-destructive">{results.filter(r => !r.success).length}</p>
                  <p className="text-xs text-destructive">{t('businessRoles.bulkFailed')}</p>
                </div>
              </div>

              {/* Per-employee results */}
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {results.map((r) => (
                  <div key={r.employeeId} className={`flex items-center justify-between p-2.5 rounded-lg border ${
                    r.success ? 'border-success/20 bg-success/5' : 'border-destructive/20 bg-destructive/5'
                  }`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        r.success ? 'bg-success/20' : 'bg-destructive/20'
                      }`}>
                        {r.success ? (
                          <svg className="w-3.5 h-3.5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-3.5 h-3.5 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{r.employeeName || r.employeeId}</p>
                        {!r.success && r.errorMessage && (
                          <p className="text-xs text-destructive">{r.errorMessage}</p>
                        )}
                        {r.conflicts.length > 0 && (
                          <p className="text-xs text-warning">
                            {r.conflicts.length} {t('businessRoles.bulkConflictsFound')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {step === 'select' && (
            <>
              <Button variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleProceedToConfirm}
                disabled={selectedEmployees.length === 0}
              >
                {t('businessRoles.bulkContinue')} ({selectedEmployees.length})
              </Button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => setStep('select')} disabled={assigning}>
                {t('common.back')}
              </Button>
              <Button onClick={handleAssign} loading={assigning}>
                {t('businessRoles.bulkAssignButton', { count: selectedEmployees.length })}
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button onClick={onClose}>
              {t('common.close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
